// Checkout Page - Place orders for cart items
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  ArrowLeft,
  Package,
  Truck,
  MapPin,
  Phone,
  AlertCircle,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import {
  Button,
  useToast,
  VanStockToggle,
} from '../../components/radix'
import { FulfillmentDateRow } from '../../components/engineer/checkout/FulfillmentDatePicker'
import { DeliveryPostcodeNotices } from '../../components/engineer/checkout/DeliveryPostcodeNotices'
import { UKAddressLookup } from '../../components/UKAddressLookup'
import { useCart } from '../../auth/hooks/useCart'
import { useWeeklySpend } from '../../auth/hooks/useWeeklySpend'
import { useMe } from '../../auth/hooks/useMe'
import { useDeliveryPostcodeCheck, type DeliveryPostcodeStatus } from '../../hooks/useDeliveryPostcodeCheck'
import { useMergeSameDeliveryLines } from '../../hooks/useMergeSameDeliveryLines'
import { api } from '../../services/api'
import { updateProfile } from '../../auth/services/auth-api'
import { getAppEnv } from '../../lib/env'
import {
  toAmount,
  trackCheckoutStarted,
} from '../../lib/analytics'
import {
  checkoutScope,
  clearCheckoutId,
  getOrMintCheckoutId,
  shouldFireCheckoutStarted,
} from '../../lib/checkout-session'
import { normalizePostcode } from '../../services/postcodes'
import { formatMoney } from '../../utils/currency'
import type { CartItem, FulfillmentOption, PreferredDateCapability } from '../../types'
import type { TenantOrderConfig } from '../../auth/types'
import { perUnitPrice as perUnit, roundToPenny } from '../../utils/pricing'
import { toastClearanceRef } from '../../utils/heightVarRef'
import { checkoutOrderDates, NO_PREFERRED_DATE } from '../../utils/fulfillmentDates'

const POSTCODE_NOT_FOUND_MESSAGE = "We couldn't find that postcode."

// Suppliers whose delivery branch is postcode-driven (DEV-52059). For these,
// when the engineer edits the delivery postcode on checkout and it differs
// from the job postcode, we re-check stock using the delivery postcode and
// override the recorded branch if the supplier would fulfil from a different
// one. IDs match suppliers table seed: 1 = Wolseley, 2 = City Plumbing.
const POSTCODE_ROUTED_SUPPLIER_IDS = new Set<string>(['1', '2'])

interface DeliveryBranchOverride {
  oldBranchId: string
  oldBranchName: string
  newBranchId: string
  newBranchName: string
  supplierName: string
}

interface DeliveryBranchStockEntry {
  raw_product_id: number | string
  branch_id: string | number
  branch_name?: string
  delivery_source_branch_id?: string | number | null
  delivery_source_branch_name?: string | null
  stock_unavailable?: boolean
  // Fields used by option B to override per-item date/stock/backorder in the
  // submission payload when the delivery-postcode branch differs. Mirrors the
  // shape cartWithLiveData reads from the main stock query.
  stock_quantity?: number
  branch_stock?: number | null
  delivery_stock?: number | null
  delivery_dc_stock?: number | null
  dc_stock?: number | null
  earliest_delivery?: string | null
  delivery_dc_date?: string | null
  backorder_delivery_date?: string | null
  can_deliver?: boolean
}

interface EffectiveDeliveryFromEntry {
  /** Effective delivery date for this line: DC date when the branch stock
   * can't cover the qty, otherwise earliest_delivery; the backorder date
   * when the qty exceeds everything the channel can supply. */
  deliveryOrderDate: string | null
  /** Delivery capacity under the new branch (branch + depot + borrow). */
  deliveryStock: number
  /** Backorder date from the delivery-postcode entry, when the supplier
   * returned one. */
  backorderDeliveryDate: string | null
  /** True when this line is backordered under the new branch. */
  isDeliveryBackorder: boolean
}

// Mirrors the delivery derivation in cartWithLiveData — same DC-date rule, same
// max-across-sources capacity, same backorder gate — but applied to a specific
// stock entry (the delivery-postcode response) rather than the component-level
// liveStockData. Used when a CPS/Wolseley group is overridden to the delivery-
// postcode branch so the order's recorded date/stock/backorder values match
// the branch the supplier actually fulfils from (DEV-52059 option B).
function computeEffectiveDeliveryFromEntry(
  item: CartItem,
  entry: DeliveryBranchStockEntry,
): EffectiveDeliveryFromEntry {
  const stockQuantity = entry.stock_quantity ?? 0
  const branchStock = entry.branch_stock ?? null
  const canDeliver = entry.can_deliver !== false
  const needsDcDates = branchStock != null && item.quantity > branchStock
  const deliveryDcDate = entry.delivery_dc_date ?? null
  const deliveryDate = needsDcDates ? deliveryDcDate : (entry.earliest_delivery ?? null)
  const branchDeliveryQty = entry.delivery_stock ?? stockQuantity
  const deliveryQty = !canDeliver
    ? 0
    : Math.max(
        branchDeliveryQty,
        entry.delivery_dc_stock ?? 0,
        entry.dc_stock ?? 0,
      ) || branchDeliveryQty
  const backorderDeliveryDate = entry.backorder_delivery_date ?? null
  // Backorder policy is supplier/product-level, so item.backorderAllowed is
  // branch-invariant; the date and the stock gap come from the new branch.
  const isDeliveryBackorder =
    item.backorderAllowed === true &&
    canDeliver &&
    item.quantity > deliveryQty &&
    backorderDeliveryDate != null
  const deliveryOrderDate = isDeliveryBackorder ? backorderDeliveryDate : deliveryDate
  return {
    deliveryOrderDate,
    deliveryStock: deliveryQty,
    backorderDeliveryDate,
    isDeliveryBackorder,
  }
}

interface DeliveryBranchResult {
  overrides: Map<string, DeliveryBranchOverride>
  // True when at least one postcode-routed group's lookup didn't produce a
  // usable answer — a missing row, or an unavailable row (/api/suppliers/
  // cart-stock/ returns HTTP 200 with stock_unavailable: true when a supplier
  // call fails, so React Query's isError alone misses this case).
  hasFailure: boolean
}

// Shared derivation used by both the UI notice and the Place order handler
// (DEV-52059). A CPS/Wolseley group under one job postcode is a single branch
// and the delivery-postcode lookup resolves to one fulfilment branch for the
// supplier, so the group's first item is representative — mixed sources
// across items in one group don't happen in practice. The lookup FAILING for
// that item (unavailable or missing) does, and surfaces as hasFailure.
function computeDeliveryBranchResult(
  stock: DeliveryBranchStockEntry[] | undefined,
  orders: BranchOrder[],
): DeliveryBranchResult {
  const overrides = new Map<string, DeliveryBranchOverride>()
  let hasFailure = false
  if (!stock) return { overrides, hasFailure }
  for (const order of orders) {
    if (!POSTCODE_ROUTED_SUPPLIER_IDS.has(String(order.supplierId))) continue
    if (order.branchId == null) continue
    const firstItem = order.items[0]
    if (!firstItem) continue
    const entry = stock.find(
      (s) =>
        String(s.raw_product_id) === String(firstItem.productId) &&
        String(s.branch_id) === String(firstItem.branchId),
    )
    if (!entry || entry.stock_unavailable === true) {
      hasFailure = true
      continue
    }
    // Effective fulfilment branch at the delivery postcode: borrow source
    // when the supplier marked one, otherwise the branch itself. Mirrors
    // what groupOrdersByBranch uses on the delivery tab.
    const newBranchIdRaw = entry.delivery_source_branch_id ?? entry.branch_id
    if (newBranchIdRaw == null || String(newBranchIdRaw) === '') {
      hasFailure = true
      continue
    }
    const newBranchId = String(newBranchIdRaw)
    if (newBranchId === String(order.branchId)) continue
    const newBranchName = String(
      entry.delivery_source_branch_name ?? entry.branch_name ?? newBranchId,
    )
    overrides.set(`${order.supplierId}-${order.branchId}`, {
      oldBranchId: String(order.branchId),
      oldBranchName: order.branchName,
      newBranchId,
      newBranchName,
      supplierName: order.supplierName,
    })
  }
  return { overrides, hasFailure }
}

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

interface BranchOrder {
  supplierId: string | number
  branchId: string | number | null
  supplierName: string
  branchName: string
  distanceMiles: number | null
  items: CartItem[]
  total: number
  hasUnavailablePrice?: boolean  // True if any item has unavailable price
  // DEV-51018: every item in this order is on backorder in the chosen
  // channel. The split happens in the grouper so each order gets its own
  // date, own approval evaluation, and its own supplier PO.
  isBackorder: boolean
  // DEV-51011 AC13: whether the supplier takes a chosen date, and how far ahead.
  preferredDate: PreferredDateCapability
  branchAddressLine1?: string
  branchAddressLine2?: string
  branchCity?: string
  branchPostcode?: string
  branchPhone?: string
}

// Per-unit price for a checkout line — delegates to the shared pricing helper
// so the "divide by multiple" rule has a single definition.
const perUnitPrice = (item: { price: number; quantityMultiple?: number }): number =>
  perUnit(item.price, item.quantityMultiple)

// Note: Cart is now fetched from backend via useCart() hook

// Group cart items by branch and filter by fulfillment
// A line can be placed on backorder for a channel only when the supplier/
// product allows it AND that channel has an availability date (no date —
// e.g. Wolseley's API returned none — keeps the line blocked as before).
function canBackorderLine(item: CartItem, fulfillmentType: 'pickup' | 'delivery'): boolean {
  if (item.backorderAllowed !== true) return false
  return fulfillmentType === 'pickup'
    ? item.canCollect !== false && item.backorderCollectionDate != null
    : item.canDeliver !== false && item.backorderDeliveryDate != null
}

// DEV-51018: is this line ACTUALLY on backorder in the chosen channel,
// right now? Grouper keys on this so a supplier+branch basket becomes an
// in-stock order + a separate backorder order — the in-stock lines
// aren't held up behind the SLA. Uses the flag stamped by checkoutItems;
// falls back to the qty-vs-stock check when the flag is missing (older
// cart snapshots).
function isBackorderNow(item: CartItem, fulfillmentType: 'pickup' | 'delivery'): boolean {
  if (!canBackorderLine(item, fulfillmentType)) return false
  const flag = fulfillmentType === 'pickup' ? item.isPickupBackorder : item.isDeliveryBackorder
  if (flag !== undefined) return flag === true
  const option = item.fulfillmentOptions?.find((opt) => opt.type === fulfillmentType)
  const stock = option?.stockQuantity ?? 0
  return item.quantity > stock
}

function groupOrdersByBranch(
  cartItems: CartItem[],
  fulfillmentType: 'pickup' | 'delivery'
): BranchOrder[] {
  const groups = new Map<string, BranchOrder>()

  // Filter items that support selected fulfillment
  // If fulfillmentOptions is empty, assume items are available (Cart already validated)
  const availableItems = cartItems.filter((item) => {
    // If no fulfillmentOptions data, trust that Cart already validated availability
    if (!item.fulfillmentOptions || item.fulfillmentOptions.length === 0) {
      return true
    }

    // If we have fulfillmentOptions, check if selected fulfillment is available
    if (fulfillmentType === 'pickup' && item.canCollect === false) return false
    if (fulfillmentType === 'delivery' && item.canDeliver === false) return false
    // Backorder: above-stock (or zero-stock) lines are still placeable when
    // the supplier/product allows it AND the channel has an availability date.
    // Never while stock is unknown.
    if (canBackorderLine(item, fulfillmentType)) return true
    const option = item.fulfillmentOptions.find((opt) => opt.type === fulfillmentType)
    return option?.available && option.stockQuantity > 0 && item.quantity <= option.stockQuantity
  })

  availableItems.forEach((item) => {
    // On the delivery tab, group by the delivering branch (borrowed source ??
    // own branch), matching the cart. Same-product lines delivered from one
    // branch are merged into one cart line beforehand
    // (useMergeSameDeliveryLines), so a group holds each product once.
    const useDeliveryBranch =
      fulfillmentType === 'delivery' && item.deliverySourceBranchId != null
    const groupBranchId = useDeliveryBranch ? item.deliverySourceBranchId : item.branchId
    const groupBranchName = useDeliveryBranch
      ? (item.deliverySourceBranchName ?? item.branchName)
      : item.branchName
    const groupDistance = useDeliveryBranch
      ? (item.deliverySourceBranchDistanceMiles ?? null)
      : item.distanceMiles
    // DEV-51018: backorder position is part of the key so a
    // supplier+branch group with mixed stock produces two orders —
    // in-stock first, backorder second — each with its own date and its
    // own supplier PO.
    const backorder = isBackorderNow(item, fulfillmentType)
    const key = `${item.supplierId}-${groupBranchId}-${backorder ? 'bo' : 'is'}`

    if (!groups.has(key)) {
      groups.set(key, {
        supplierId: item.supplierId,
        branchId: groupBranchId,
        supplierName: item.supplierName,
        branchName: groupBranchName,
        distanceMiles: groupDistance,
        isBackorder: backorder,
        preferredDate: item.preferredDate ?? NO_PREFERRED_DATE,
        items: [],
        total: 0,
        branchAddressLine1: item.branchAddressLine1,
        branchAddressLine2: item.branchAddressLine2,
        branchCity: item.branchCity,
        branchPostcode: item.branchPostcode,
        branchPhone: item.branchPhone,
      })
    }

    const group = groups.get(key)!
    group.items.push(item)
    // Only add to total if price is available. Round each line to pennies before
    // summing so the group total equals the sum of the rounded lines the engineer
    // sees — the same round-then-sum create_order persists (avoids a penny drift
    // on pack products with a fractional per-unit price).
    if (!item.priceUnavailable) {
      group.total += roundToPenny(perUnitPrice(item) * item.quantity)
    }
    // Track if any item has unavailable price
    group.hasUnavailablePrice = group.items.some(i => i.priceUnavailable)
  })

  return Array.from(groups.values()).sort((a, b) => {
    // DEV-51018: for the same supplier+branch, the in-stock order is
    // placed above its backorder sibling so the engineer confirms the
    // today-collectable one first.
    if (
      a.supplierId === b.supplierId &&
      String(a.branchId) === String(b.branchId) &&
      a.isBackorder !== b.isBackorder
    ) {
      return a.isBackorder ? 1 : -1
    }
    if (a.distanceMiles === null) return 1
    if (b.distanceMiles === null) return -1
    return a.distanceMiles - b.distanceMiles
  })
}

// DEV-51018: identity key for an expandable order card. Card render and
// expandAllOrders both derive their keys from this single helper so a
// future field on BranchOrder can't cause the two sides to drift.
// A supplier+branch can produce two sibling orders (in-stock and
// backorder), so isBackorder is part of the key.
function orderKeyFor(order: Pick<BranchOrder, 'supplierId' | 'branchId' | 'isBackorder'>): string {
  return `${order.supplierId}-${order.branchId}-${order.isBackorder ? 'bo' : 'is'}`
}

// UK phone validation (mobile + landline). Whitespace is stripped before
// matching, so the pattern itself is space-free.
const UK_PHONE_REGEX = /^(?:\+44|0044|0)[1-9]\d{8,9}$/

/**
 * Derives a validation regex from a job number placeholder used as a format mask.
 * Each alphanumeric char in the placeholder accepts any letter or digit.
 * Each separator char (dash, slash, etc.) must appear literally at that position.
 * Returns null when the placeholder is blank or contains spaces (descriptive hint).
 */
function deriveFormatRegex(placeholder: string): RegExp | null {
  if (!placeholder || placeholder.includes(' ')) return null
  const pattern = placeholder
    .split('')
    .map((char) =>
      /[A-Za-z0-9]/.test(char) ? '[A-Za-z0-9]' : char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )
    .join('')
  return new RegExp(`^${pattern}$`, 'i')
}

export function CheckoutPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  // Read from URL params (survives page refresh)
  const fulfillmentMode = (searchParams.get('mode') as 'pickup' | 'delivery') || 'pickup'
  const branchIdParam = searchParams.get('branch')
  // DEV-51018: `bo=1` marks the backorder sibling of a supplier+branch's
  // two split checkouts. Used only to scope the checkout_id — the actual
  // in-stock/backorder split is re-derived from live stock in
  // groupOrdersByBranch, so this flag never drives item classification.
  const isBackorderCheckout = searchParams.get('bo') === '1'
  // Exact cart-line ids for this checkout (a delivery group can span original
  // branches, so it can't be reconstructed by branch alone). Falls back to the
  // branch filter when absent.
  const itemIdsParam = searchParams.get('items')

  // Use backend cart
  const {
    cart: backendCart,
    isLoading: isLoadingCart,
    updateVanStockToggle,
    mergeCartLines,
  } = useCart()
  const { data: weeklySpend } = useWeeklySpend()
  const { addToast } = useToast()
  const { data: meData } = useMe()

  // Refresh `auth-me` once when checkout mounts so the engineer always sees
  // the latest tenant order config (reason field type / requirement /
  // dropdown options). The cached value is shown immediately while the
  // refetch happens in the background — no loading flicker.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['auth-me'] })
  }, [queryClient])

  // Get order config from user's tenant (via /api/users/me/)
  // Engineers can access this endpoint, unlike /api/tenants/ which requires settings.view permission
  const orderConfig: TenantOrderConfig = meData?.tenant?.order_config ?? {
    reason_field_type: 'text',
    reason_field_requirement: 'optional',
    reason_dropdown_options: null,
    secondary_reason_field_requirement: 'required',
    secondary_reason_dropdown_options: null,
    job_number_requirement: 'optional',
    job_number_placeholder: 'e.g. J-A1B2C3D4',
  }

  const [session, setSession] = useState<EngineerSession | null>(null)
  const [cart, setCart] = useState<CartItem[]>([])
  const [fulfillmentType, setFulfillmentType] = useState<'pickup' | 'delivery'>(fulfillmentMode)
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)
  const [hasPlacedOrder, setHasPlacedOrder] = useState(false)
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set())
  const [showSupplierOrderWarning, setShowSupplierOrderWarning] = useState(false)

  // Phone number dialog state
  const [showPhoneDialog, setShowPhoneDialog] = useState(false)
  const [phoneValue, setPhoneValue] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [isSavingPhone, setIsSavingPhone] = useState(false)
  // Ref flag: skip the phone check on the re-call immediately after saving
  const skipPhoneCheckRef = useRef(false)

  // Order details
  const [jobNumber, setJobNumber] = useState('')
  const [reasonForOrder, setReasonForOrder] = useState('')
  const [secondaryReason, setSecondaryReason] = useState('')

  // Validation errors
  const [errors, setErrors] = useState({
    jobNumber: '',
    reasonForOrder: '',
    secondaryReason: '',
    deliveryAddress: '',
  })

  // Refs for scrolling to errors
  const jobNumberRef = useRef<HTMLDivElement>(null)
  const reasonRef = useRef<HTMLDivElement>(null)
  const deliveryAddressRef = useRef<HTMLDivElement>(null)

  // Delivery address state (UK format from Ideal Postcodes)
  const [deliveryAddress, setDeliveryAddress] = useState({
    line_1: '',
    line_2: '',
    line_3: '',
    post_town: '',
    postcode: '',
  })

  // DEV-51047: the delivery postcode is editable, so check a changed one is a
  // real postcode. Only a definite "doesn't exist" blocks Place order; a check
  // that can't run never does.
  const deliveryPostcodeCheck = useDeliveryPostcodeCheck(
    deliveryAddress.postcode,
    session?.postcode ?? '',
  )
  const [isSettlingPostcode, setIsSettlingPostcode] = useState(false)
  // Same double-click guard as isCheckingSupplierWarningRef: the postcode
  // check is awaited before isPlacingOrder disables the button.
  const isSettlingPostcodeRef = useRef(false)

  // Swap what the engineer typed for the tidy spelling ("so160as" ->
  // "SO16 0AS") once found, unless they've typed something else since.
  useEffect(() => {
    const canonical = deliveryPostcodeCheck.postcode
    if (deliveryPostcodeCheck.status !== 'found' || !canonical) return
    setDeliveryAddress((prev) =>
      prev.postcode !== canonical && normalizePostcode(prev.postcode) === normalizePostcode(canonical)
        ? { ...prev, postcode: canonical }
        : prev,
    )
  }, [deliveryPostcodeCheck.status, deliveryPostcodeCheck.postcode])

  // The address list follows the delivery postcode once it's known to be
  // real; while it's being typed, unknown or unchecked there's no list, so an
  // engineer can't pick an address that belongs to a different postcode.
  const addressListPostcode =
    deliveryPostcodeCheck.status === 'idle'
      ? (session?.postcode ?? '')
      : deliveryPostcodeCheck.status === 'found'
        ? (deliveryPostcodeCheck.postcode ?? '')
        : ''

  // Van stock toggle state (Map of cart item ID -> boolean)
  const [vanStockToggles, setVanStockToggles] = useState<Map<string, boolean>>(new Map())

  // DEV-51011: the day the engineer chose per order card, keyed by
  // orderKeyFor(order). Absent = the order's earliest date. Re-validated on
  // every render by checkoutOrderDates, so a stock refetch can't leave a stale
  // choice behind.
  const [chosenDates, setChosenDates] = useState<Record<string, string>>({})

  // Scroll to top on mount so navigation from the cart doesn't land the
  // engineer partway down the checkout form.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Initialize van stock toggles when cart loads
  useEffect(() => {
    if (cart.length > 0) {
      const initialToggles = new Map<string, boolean>()
      cart.forEach(item => {
        const itemId = item.id || String(item.productId)
        // Prefer engineer's saved DB toggle, fall back to admin designation
        const toggled = item.isVanStockToggled ?? item.isVanStock ?? false
        initialToggles.set(itemId, toggled)
      })
      setVanStockToggles(initialToggles)
    }
  }, [cart])

  // Helper to check if item is toggled as van stock
  const isItemVanStock = (itemId: string): boolean => {
    return vanStockToggles.get(itemId) || false
  }

  // Toggle handler — updates local state and persists to DB so cart page reflects the change
  const handleVanStockToggle = (itemId: string) => {
    const newValue = !vanStockToggles.get(itemId)
    setVanStockToggles(prev => {
      const next = new Map(prev)
      next.set(itemId, newValue)
      return next
    })
    updateVanStockToggle(itemId, newValue)
  }

  // Compute cart composition
  const cartItemIds = cart.map(item => item.id || String(item.productId))
  const vanStockItemIds = cartItemIds.filter(itemId => isItemVanStock(itemId))
  const hasMixedCart = vanStockItemIds.length > 0 && vanStockItemIds.length < cart.length
  const hasNoVanStock = vanStockItemIds.length === 0

  // Check if ANY item in cart is marked as van stock by admin (initial state)
  const hasAnyVanStockProducts = cart.some(item => item.isVanStock)

  // Update fulfillment type when URL param changes
  useEffect(() => {
    setFulfillmentType(fulfillmentMode)
  }, [fulfillmentMode])

  // Load session on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      const parsedSession = JSON.parse(savedSession)
      setSession(parsedSession)
      // Initialize postcode from session (other fields empty until user selects/enters)
      setDeliveryAddress((prev) => ({
        ...prev,
        postcode: parsedSession.postcode,
      }))
    } else {
      navigate(`/${tenantSlug}/engineer`)
    }
  }, [navigate, tenantSlug])

  // Sync cart from backend when backendCart changes
  useEffect(() => {
    // Wait for backend cart to load
    if (isLoadingCart) return

    // Don't redirect if we've already placed an order (navigating to confirmation)
    if (hasPlacedOrder) return

    // Redirect if cart is empty (user arrived with no items)
    if (backendCart.length === 0) {
      navigate(`/${tenantSlug}/engineer/cart`)
      return
    }

    // Scope the checkout to this group's exact items (preferred — a delivery
    // group can span original branches), falling back to the branch filter.
    const itemIdSet = itemIdsParam ? new Set(itemIdsParam.split(',')) : null
    const filteredCart = itemIdSet
      ? backendCart.filter((item) => !!item.id && itemIdSet.has(String(item.id)))
      : branchIdParam
        ? backendCart.filter((item) => String(item.branchId) === branchIdParam)
        : backendCart

    setCart(filteredCart)
  }, [backendCart, isLoadingCart, navigate, tenantSlug, branchIdParam, itemIdsParam, hasPlacedOrder])

  // Prepare cart items for stock fetch (raw_product_id + branch_id)
  const cartStockItems = useMemo(() =>
    cart
      .filter(item => item.branchId !== null)
      .map(item => ({
        raw_product_id: item.productId,
        branch_id: String(item.branchId),
        quantity: item.quantity,
      })),
    [cart]
  )

  // Fetch live stock for all cart items
  const {
    data: liveStockData,
    isLoading: isLoadingStock,
    isFetching: isFetchingStock,
  } = useQuery({
    queryKey: ['checkout-stock', JSON.stringify(cartStockItems), session?.postcode],
    queryFn: () => api.getCartStock(
      cartStockItems,
      session!.postcode,
      session!.latitude,
      session!.longitude
    ),
    enabled: cart.length > 0 && !!session?.postcode && cartStockItems.length > 0,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes (shorter than cart - checkout is more critical)
    refetchOnWindowFocus: true, // Always refetch when user returns to tab
  })

  // Fetch live prices for all cart items
  const rawProductIds = useMemo(() =>
    cart.map(item => Number(item.productId)),
    [cart]
  )

  const { data: livePriceData, isLoading: isLoadingPrice } = useQuery({
    queryKey: ['checkout-prices', rawProductIds.join(',')],
    // Checkout is the commit point: ask the supplier every time rather than
    // reusing a recent answer. The fetch also refreshes the shared answer, so
    // placing the order moments later reuses it instead of calling again.
    queryFn: () => api.getLivePrices(rawProductIds, true),
    enabled: cart.length > 0 && rawProductIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Merge cart items with live stock and price data
  const cartWithLiveData: CartItem[] = useMemo(() => {
    if (!liveStockData?.stock && !livePriceData?.prices) return cart

    return cart.map(item => {
      // Find stock entry for this item's product + branch combination
      const stockEntry = liveStockData?.stock?.find(
        s => String(s.raw_product_id) === String(item.productId) &&
             String(s.branch_id) === String(item.branchId)
      )

      // Find live price for this item
      const priceEntry = livePriceData?.prices?.find(
        p => String(p.raw_product_id) === String(item.productId)
      )
      const priceUnavailable = priceEntry?.unavailable === true
      const livePrice = (priceEntry?.selling_price && !priceUnavailable)
        ? parseFloat(priceEntry.selling_price)
        : item.price
      // Use 1p tolerance to avoid false warnings from floating point precision differences
      // e.g. 0.32 vs 0.318 should not trigger a warning (difference < £0.01)
      const priceChanged = !priceUnavailable && Math.abs(livePrice - item.price) >= 0.01
      const oldPrice = item.price  // Store original cart price for display
      // CEF returns a fresh quantity_multiple alongside its live pack price;
      // other suppliers omit it, so the cached catalogue value is preserved.
      const liveMultiple = priceEntry?.quantity_multiple ?? item.quantityMultiple

      if (!stockEntry) {
        // No stock data for this item - mark as unavailable. Stock unknown
        // also means no backorder (never trust the cart's cached flag here).
        return {
          ...item,
          priceAtAdded: oldPrice,  // Store original price
          price: livePrice,
          quantityMultiple: liveMultiple,
          priceChanged, // Track if price changed since cart
          priceUnavailable,
          backorderAllowed: false,
          fulfillmentOptions: [
            { type: 'pickup' as const, available: false, stockQuantity: 0, stockStatus: 'out_of_stock' as const },
            { type: 'delivery' as const, available: false, stockQuantity: 0, stockStatus: 'out_of_stock' as const },
          ],
        }
      }

      const stockQuantity = stockEntry.stock_quantity ?? 0
      const dcStock = stockEntry.dc_stock ?? null
      const branchStock = stockEntry.branch_stock ?? null
      const totalStock = stockEntry.total_stock ?? null
      const dcAwareMax = dcStock != null
        ? Math.max(branchStock ?? stockQuantity, dcStock)
        : stockQuantity
      // Prefer split fulfilment stock from backend; fall back to the combined
      // value so legacy adapters that don't split still render correctly.
      const collectionQty = stockEntry.collection_stock ?? stockQuantity
      const canCollect = stockEntry.can_collect !== false
      const pickupQty = canCollect
        ? (dcStock != null ? dcAwareMax : collectionQty)
        : 0
      const canDeliver = stockEntry.can_deliver !== false
      // Delivery capacity uses the same DC-backed rule as Cart's
      // getAvailableStock and ProductDetail's deliveryMaxQty:
      // max(delivery_stock, delivery_dc_stock, dc_stock). This keeps Cart and
      // Checkout in agreement — otherwise Cart accepts a qty backed by DC
      // capacity, then Checkout's stricter cap silently drops the line from
      // availableItems and the engineer sees an empty checkout. CPS puts the
      // DC number in delivery_dc_stock; Wolseley puts it in dc_stock — reading
      // both covers every adapter.
      const deliveryDcStock = (stockEntry as any).delivery_dc_stock ?? null
      const branchDeliveryQty = stockEntry.delivery_stock ?? stockQuantity
      const deliveryQty = !canDeliver
        ? 0
        : Math.max(
            branchDeliveryQty,
            deliveryDcStock ?? 0,
            dcStock ?? 0,
          ) || branchDeliveryQty
      // Per-channel date rule: when the requested qty fits within branch
      // stock, use the branch's own dates; when it exceeds branch stock the
      // order is DC-fulfilled, so use the explicit DC dates. Applied to BOTH
      // the displayed earliestDate and the order-placement date so they agree.
      const pickupDcDate = (stockEntry as any).pickup_dc_date ?? null
      const deliveryDcDate = (stockEntry as any).delivery_dc_date ?? null
      const needsDcDates = branchStock != null && item.quantity > branchStock
      const pickupDate = needsDcDates
        ? pickupDcDate
        : (stockEntry.earliest_collection ?? null)
      const deliveryDate = needsDcDates
        ? deliveryDcDate
        : (stockEntry.earliest_delivery ?? null)
      // Backorder tier — backend-decided; absent on older responses = off.
      // When the qty exceeds everything the channel can supply (branch, depot,
      // borrowed branch), the line's order date is the backorder date.
      const backorderAllowed = stockEntry.backorder_allowed === true
      const backorderCollectionDate = stockEntry.backorder_collection_date ?? null
      const backorderDeliveryDate = stockEntry.backorder_delivery_date ?? null
      const isPickupBackorder =
        backorderAllowed && canCollect && item.quantity > pickupQty && backorderCollectionDate != null
      const isDeliveryBackorder =
        backorderAllowed && canDeliver && item.quantity > deliveryQty && backorderDeliveryDate != null
      const pickupOrderDate = isPickupBackorder ? backorderCollectionDate : pickupDate
      const deliveryOrderDate = isDeliveryBackorder ? backorderDeliveryDate : deliveryDate
      const fulfillmentOptions: FulfillmentOption[] = [
        {
          type: 'pickup' as const,
          available: pickupQty > 0,
          stockQuantity: pickupQty,
          stockStatus: pickupQty > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: pickupDate,
        },
        {
          type: 'delivery' as const,
          available: deliveryQty > 0,
          stockQuantity: deliveryQty,
          stockStatus: deliveryQty > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: deliveryDate,
        },
      ]

      return {
        ...item,
        priceAtAdded: oldPrice,  // Store original price
        price: livePrice,
        quantityMultiple: liveMultiple,
        priceChanged, // Track if price changed since cart
        priceUnavailable,
        fulfillmentOptions,
        maxStock: dcAwareMax,
        branchStock,
        dcStock,
        totalStock,
        pickupOrderDate,
        deliveryOrderDate,
        canCollect,
        canDeliver,
        backorderAllowed,
        backorderCollectionDate,
        backorderDeliveryDate,
        // DEV-51018: per-channel "on backorder now" — grouper uses these
        // to split each supplier+branch into an in-stock and a backorder
        // order card.
        isPickupBackorder,
        isDeliveryBackorder,
        // Borrowed delivery source branch (present only on borrow rows). Used
        // for the delivery-tab header and to place the order on that branch.
        deliverySourceBranchId: (stockEntry as any).delivery_source_branch_id ?? null,
        deliverySourceBranchName: (stockEntry as any).delivery_source_branch_name ?? null,
        deliverySourceBranchDistanceMiles:
          (stockEntry as any).delivery_source_branch_distance_miles ?? null,
        distanceMiles: stockEntry.distance_miles ?? item.distanceMiles,
        // Prefer the supplier's live UOM when the stock response carries
        // one (currently Travis Perkins). Other suppliers leave it null,
        // so the cached catalogue value is preserved.
        unitOfMeasure: stockEntry.uom ?? item.unitOfMeasure,
      }
    })
  }, [cart, liveStockData, livePriceData])

  // Safety net for the basket's merge (it may still be in flight, or checkout's
  // quantity-aware stock can name a different delivering branch): lines of
  // one product delivered from the same branch become one line before the
  // order is placed. Only lines with a live stock answer take part. Place
  // order waits while a merge is due or running and until stock is reloaded
  // for the combined quantity; a merge that failed keeps it blocked, since the
  // split lines were never stock-checked on their total.
  const {
    isSettling: isMergeSettling,
    unmergedProductNames,
  } = useMergeSameDeliveryLines({
    items: cartWithLiveData.filter((item) =>
      liveStockData?.stock?.some(
        (entry) =>
          String(entry.raw_product_id) === String(item.productId) &&
          String(entry.branch_id) === String(item.branchId),
      ),
    ),
    getMethod: () => fulfillmentType,
    isReady:
      !isLoadingStock &&
      !isFetchingStock &&
      !!liveStockData?.stock &&
      !isPlacingOrder &&
      !hasPlacedOrder,
    isLineBusy: () => false,
    mergeCartLines,
  })

  const orders = groupOrdersByBranch(cartWithLiveData, fulfillmentType)

  // DEV-52059: CPS/Wolseley return the delivery branch per postcode. The main
  // stock query above uses the job postcode. When the engineer edits the
  // delivery postcode on checkout we fire a second, read-only stock call with
  // the delivery postcode (CPS/Wolseley items only) so we can warn if the
  // supplier would fulfil from a different branch — and send `order_branch_id`
  // so our record matches what the supplier actually uses. This query never
  // writes to the cart, prices, or the UI's displayed stock values.
  const postcodeRoutedStockItems = useMemo(
    () =>
      cartStockItems.filter((stockItem) => {
        const cartLine = cart.find(
          (c) =>
            String(c.productId) === String(stockItem.raw_product_id) &&
            String(c.branchId) === String(stockItem.branch_id),
        )
        return cartLine
          ? POSTCODE_ROUTED_SUPPLIER_IDS.has(String(cartLine.supplierId))
          : false
      }),
    [cartStockItems, cart],
  )

  const canonicalDeliveryPostcode =
    deliveryPostcodeCheck.status === 'found'
      ? (deliveryPostcodeCheck.postcode ?? '')
      : ''

  const deliveryPostcodeDiffersFromJob =
    fulfillmentType === 'delivery' &&
    canonicalDeliveryPostcode !== '' &&
    normalizePostcode(canonicalDeliveryPostcode) !==
      normalizePostcode(session?.postcode ?? '')

  const {
    data: deliveryBranchStockData,
    isError: deliveryBranchStockIsError,
  } = useQuery({
    queryKey: [
      'checkout-stock-delivery-postcode',
      JSON.stringify(postcodeRoutedStockItems),
      canonicalDeliveryPostcode,
    ],
    queryFn: () =>
      api.getCartStock(postcodeRoutedStockItems, canonicalDeliveryPostcode),
    enabled:
      deliveryPostcodeDiffersFromJob && postcodeRoutedStockItems.length > 0,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  // Per-supplier-group branch overrides keyed by `${supplierId}-${branchId}`
  // (the branch the UI currently shows). Driven by the delivery-postcode
  // stock call via computeDeliveryBranchResult (shared with the Place order
  // handler so the UI warning and the submitted order_branch_id can't drift).
  const { overrides: deliveryBranchOverrides, hasFailure: deliveryBranchHasFailure } =
    useMemo(() => {
      if (
        !deliveryPostcodeDiffersFromJob ||
        fulfillmentType !== 'delivery' ||
        !deliveryBranchStockData?.stock
      ) {
        return { overrides: new Map(), hasFailure: false }
      }
      return computeDeliveryBranchResult(
        deliveryBranchStockData.stock as DeliveryBranchStockEntry[],
        orders,
      )
    }, [
      deliveryPostcodeDiffersFromJob,
      fulfillmentType,
      deliveryBranchStockData,
      orders,
    ])

  // The fallback warning fires when the delivery-postcode lookup didn't give
  // us a usable answer. React Query's isError only catches HTTP failures;
  // /api/suppliers/cart-stock/ returns 200 with stock_unavailable: true rows
  // when the supplier call itself failed, so a per-row check is also needed
  // (computed inside computeDeliveryBranchResult → hasFailure).
  const deliveryBranchLookupFailed =
    deliveryPostcodeDiffersFromJob &&
    postcodeRoutedStockItems.length > 0 &&
    (deliveryBranchStockIsError || deliveryBranchHasFailure)

  // Whether every item that will actually be ordered is van stock. Derived from
  // `orders` (the groups that get placed) rather than the raw cart, so items
  // excluded as unavailable don't keep the Job Details section shown/validated for
  // a checkout that is really all van stock. Governs the section's visibility and
  // validation; the per-order payload applies the same rule per branch group.
  const allItemsVanStock =
    orders.length > 0 &&
    orders.every(
      order =>
        order.items.length > 0 &&
        order.items.every(item => isItemVanStock(item.id || String(item.productId)))
    )

  // The Job Details section (job number, reason, secondary reason) is hidden for a
  // pure van stock checkout. Clear only its validation errors — not the values —
  // when that happens, so a stale error can't resurface when the section is shown
  // again after toggling van stock off. Entered values are preserved (simply not
  // submitted for a van stock order, see the payload) so a toggle-off or a failed/
  // rolled-back van stock toggle restores them.
  useEffect(() => {
    if (allItemsVanStock) {
      setErrors((prev) =>
        prev.jobNumber || prev.reasonForOrder || prev.secondaryReason
          ? { ...prev, jobNumber: '', reasonForOrder: '', secondaryReason: '' }
          : prev
      )
    }
  }, [allItemsVanStock])

  const grandTotal = orders.reduce((sum, order) => sum + order.total, 0)
  const totalItems = orders.reduce((sum, order) => sum + order.items.length, 0)
  const hasAnyUnavailablePrice = orders.some((order) => order.hasUnavailablePrice)
  // Weekly spend tracker (APR-012). spent is week-to-date, excluding this order.
  const weeklyCap = Number(weeklySpend?.weekly_cap ?? 0)
  const weeklySpent = Number(weeklySpend?.spent ?? 0)
  const weeklyRingPct = weeklyCap > 0 ? Math.round(Math.min(100, (weeklySpent / weeklyCap) * 100)) : 0
  const appEnv = getAppEnv()
  const isPreProd = appEnv === 'pre-prod'

  // Which of these branch groups would go straight to the supplier, and which
  // would be held for an approver? Evaluated server-side with the real approval
  // rules (basket value, weekly limit, restricted items, role exemption) so the
  // prediction can't drift from what create_order actually decides.
  const approvalPreviewGroups = useMemo(
    () =>
      orders.map((order) => ({
        key: String(order.branchId),
        cart_item_ids: order.items
          .map((item) => item.id)
          .filter((id): id is string => !!id),
      })),
    [orders],
  )
  const approvalPreviewKey = JSON.stringify(approvalPreviewGroups)

  // Should the live-supplier-order modal be shown for this checkout?
  //
  // Fetched at the moment of the decision rather than read from a cached value:
  // the approval inputs move independently of the basket (an earlier order gets
  // cancelled and weekly spend drops back under the cap, an admin lifts a
  // product restriction), so a prediction taken when the page loaded can be
  // wrong by the time Place Order is pressed — and a stale "everything needs
  // approval" answer would silently suppress the warning on an order that now
  // goes straight to the supplier.
  //
  // Every failure path returns true: a prediction that is missing, errored, or
  // impossible must over-warn, never under-warn. Only runs on the environments
  // that show the modal at all, so nowhere else makes the request.
  // The preview call below is the only await that runs before isPlacingOrder
  // disables the button, so without this a double-click would start two
  // placements that both pass the check and create the same orders twice. A
  // ref (not state) because it must take effect within the same tick.
  const isCheckingSupplierWarningRef = useRef(false)

  const shouldWarnBeforePlacing = async (): Promise<boolean> => {
    if (!isPreProd) return false
    if (approvalPreviewGroups.length === 0) return true
    try {
      const preview = await queryClient.fetchQuery({
        queryKey: ['approval-preview', approvalPreviewKey],
        queryFn: () => api.getApprovalPreview(approvalPreviewGroups),
        staleTime: 0,
      })
      return preview?.any_direct_to_supplier !== false
    } catch {
      return true
    }
  }

  // ── Checkout funnel analytics ─────────────────────────────────────────────
  // checkout_id is minted on the FIRST entry to checkout for this basket and
  // reused on every later checkout_* event, so revisiting checkout (e.g. going
  // back to add an item) keeps the same id. It is cleared on a successful
  // submit, which is what makes an unmatched checkout_started an abandonment.
  const checkoutIdRef = useRef<string | null>(null)
  const checkoutScopeRef = useRef<string | null>(null)
  const hasTrackedCheckoutStartRef = useRef(false)

  // Fires once the basket AND the live stock check have settled — that is
  // "the engineer reached checkout". Gated on stock (not price) because the
  // delivering branch is decided by the stock response: for a borrowed
  // delivery the item is sourced from another branch, and we only know which
  // once stock resolves. Reporting before then would record the home branch
  // while the engineer is looking at the borrowed one — a wrong event. The
  // trade is that an engineer who leaves in the first second or two, before
  // stock returns, is not counted as having started; that is accepted so the
  // branch on this event always matches the screen and order_placed.
  // isLoadingStock is false when the query is disabled (no postcode / nothing
  // to fetch) and after it succeeds OR errors, so this never hangs. basket_value
  // stays the provisional estimate (price may still be refreshing) — the
  // confirmed figure comes from the order events.
  useEffect(() => {
    if (
      hasTrackedCheckoutStartRef.current ||
      isLoadingCart ||
      isLoadingStock ||
      orders.length === 0
    ) {
      return
    }
    hasTrackedCheckoutStartRef.current = true
    // Scope the id to THIS branch checkout. The cart opens one checkout per
    // branch group, so a basket-wide id would let another branch's order join
    // this branch's checkout_started and hide a genuine abandonment.
    checkoutScopeRef.current = checkoutScope(fulfillmentType, branchIdParam, isBackorderCheckout)
    checkoutIdRef.current = getOrMintCheckoutId(checkoutScopeRef.current)

    // Fire once per basket, not once per visit. A signature of this group's
    // lines and quantities decides: an unchanged revisit stays silent, while an
    // added/removed line or a changed quantity fires again under the same id.
    // Built from `cart` — the URL-selected basket for this checkout, before
    // live-availability filtering — NOT from `orders`. `orders` drops lines the
    // live stock query filters out, and that query resolves after this effect
    // first runs, so hashing it would flip the signature between the initial
    // visit and a cached revisit even when the basket never changed. `cart` is
    // stable across the stock refresh and only moves on a real basket edit.
    const contentsSignature = cart
      .map(
        (item) =>
          `${item.id ?? `${item.productId}:${item.branchId}`}:${item.quantity}`,
      )
      .sort()
      .join('|')
    if (!shouldFireCheckoutStarted(checkoutScopeRef.current, contentsSignature)) {
      return
    }

    // Checkout is entered per branch group (Cart's "Checkout This Branch"), so
    // there is exactly one supplier/branch to report.
    const [firstOrder] = orders
    trackCheckoutStarted({
      checkoutId: checkoutIdRef.current,
      itemCount: totalItems,
      supplierCount: new Set(orders.map((order) => order.supplierId)).size,
      supplier: firstOrder.supplierName,
      supplierId: String(firstOrder.supplierId),
      // The scope branch from the URL — the branch the engineer selected on the
      // cart, which is the borrowed source on a cross-borrow and what the
      // checkout_id is scoped by, so checkout_started and checkout_abandoned
      // always agree on the branch. This effect is stock-gated, so by now the
      // grouped order has resolved to that same branch; its name is the borrowed
      // branch name shown on screen. Falls back to the group only when the URL
      // carries no branch.
      branchId:
        branchIdParam ??
        (firstOrder.branchId != null ? String(firstOrder.branchId) : null),
      branchName: firstOrder.branchName ?? cart[0]?.branchName ?? '',
      fulfillmentMode: fulfillmentType,
      postcode: session?.postcode ?? null,
      basketValue: toAmount(grandTotal),
    })
  }, [
    isLoadingCart,
    isLoadingStock,
    cart,
    orders,
    totalItems,
    grandTotal,
    fulfillmentType,
    branchIdParam,
    isBackorderCheckout,
    session,
  ])

  // Toggle order expansion
  const toggleOrder = (orderKey: string) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev)
      if (next.has(orderKey)) {
        next.delete(orderKey)
      } else {
        next.add(orderKey)
      }
      return next
    })
  }

  // Expand all orders
  const expandAllOrders = () => {
    setExpandedOrders(new Set(orders.map(orderKeyFor)))
  }

  // Collapse all orders
  const collapseAllOrders = () => {
    setExpandedOrders(new Set())
  }


  const validatePhone = (v: string): string => {
    if (!v.trim()) return 'Phone number is required'
    // Strip internal whitespace before matching so users can type the number
    // in any common format (e.g. "+44 20 3322 9128").
    if (!UK_PHONE_REGEX.test(v.replace(/\s+/g, ''))) {
      return 'Enter a valid UK phone number (e.g. 07700 900000 or 020 3322 9128)'
    }
    return ''
  }

  const handleSavePhone = async () => {
    const msg = validatePhone(phoneValue)
    if (msg) {
      setPhoneError(msg)
      return
    }
    setIsSavingPhone(true)
    try {
      await updateProfile({ phone: phoneValue.trim() })
      await queryClient.invalidateQueries({ queryKey: ['auth-me'] })
      setShowPhoneDialog(false)
      skipPhoneCheckRef.current = true
      void handlePlaceOrder()
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ||
        (err as { message?: string })?.message ||
        'Failed to save phone number'
      setPhoneError(message)
    } finally {
      setIsSavingPhone(false)
    }
  }

  // Handle place order
  const handlePlaceOrder = async (confirmedSupplierOrder = false) => {
    // Same-product lines are still being combined, or couldn't be (the button
    // is disabled meanwhile; this also covers a confirm dialog opened before).
    if (isMergeSettling || unmergedProductNames.length > 0) return
    // Open phone dialog if engineer hasn't set a phone number yet
    if (!skipPhoneCheckRef.current && !meData?.phone) {
      setPhoneValue('')
      setPhoneError('')
      setShowPhoneDialog(true)
      return
    }
    skipPhoneCheckRef.current = false

    // Finish a delivery postcode check that is still running. Never throws and
    // is capped in time, so it can only add a short wait, never stop the
    // order. A settled answer (including "couldn't check") is used as-is, so
    // an outage doesn't make every tap wait again.
    let deliveryPostcodeStatus: DeliveryPostcodeStatus = deliveryPostcodeCheck.status
    // Settle's return value is the fresh answer — read it directly rather than
    // waiting for the hook's state to re-render through to this handler
    // (DEV-52059: the branch-lookup fetch below needs the canonical postcode).
    let settledDeliveryPostcode: string | null = deliveryPostcodeCheck.postcode
    if (fulfillmentType === 'delivery' && deliveryPostcodeStatus === 'checking') {
      if (isSettlingPostcodeRef.current) return
      isSettlingPostcodeRef.current = true
      setIsSettlingPostcode(true)
      try {
        const settled = await deliveryPostcodeCheck.settle()
        deliveryPostcodeStatus = settled.status
        settledDeliveryPostcode = settled.postcode
      } finally {
        isSettlingPostcodeRef.current = false
        setIsSettlingPostcode(false)
      }
    }

    // Clear previous errors
    const newErrors = {
      jobNumber: '',
      reasonForOrder: '',
      secondaryReason: '',
      deliveryAddress: '',
    }

    let firstErrorRef: React.RefObject<HTMLDivElement | null> | null = null

    // Validate the job number. Skipped entirely for pure van stock orders, where
    // the Job Details section (and this field) is not rendered. Requirement and
    // format are independent: required enforces a non-empty value; a concrete
    // format mask (if configured) is validated only when a value is entered.
    if (!allItemsVanStock) {
      const trimmedJobNumber = jobNumber.trim()
      if (orderConfig.job_number_requirement === 'required' && !trimmedJobNumber) {
        newErrors.jobNumber = 'Job number is required'
        if (!firstErrorRef) firstErrorRef = jobNumberRef
      } else if (trimmedJobNumber) {
        const formatRegex = deriveFormatRegex(orderConfig.job_number_placeholder || '')
        if (formatRegex && !formatRegex.test(trimmedJobNumber)) {
          newErrors.jobNumber = `Job number must match the format: ${orderConfig.job_number_placeholder}`
          if (!firstErrorRef) firstErrorRef = jobNumberRef
        }
      }
    }

    // Validate Reason for Order if the tenant requires it and the field is visible.
    // The field is hidden for pure van stock orders (Job Details section is not
    // rendered when allItemsVanStock), so skip the requirement in that case —
    // otherwise placing the order fails silently on a field the user can't see.
    if (
      !allItemsVanStock &&
      orderConfig.reason_field_type !== 'hidden' &&
      orderConfig.reason_field_requirement === 'required' &&
      !reasonForOrder.trim()
    ) {
      newErrors.reasonForOrder = 'Reason for order is required'
      if (!firstErrorRef) firstErrorRef = reasonRef
    }

    // Validate Secondary Reason — only when the secondary dropdown is visible
    // (primary is a dropdown with a selected value AND the tenant configured
    // secondary options cascaded under THAT specific primary AND the tenant
    // marked the secondary as required).
    const secondaryOptionsForValidation =
      orderConfig.secondary_reason_dropdown_options?.[reasonForOrder.trim()] ?? []
    if (
      !allItemsVanStock &&
      orderConfig.reason_field_type === 'dropdown' &&
      reasonForOrder.trim() &&
      secondaryOptionsForValidation.length > 0 &&
      orderConfig.secondary_reason_field_requirement === 'required' &&
      !secondaryReason.trim()
    ) {
      newErrors.secondaryReason = 'Secondary reason is required'
      if (!firstErrorRef) firstErrorRef = reasonRef
    }

    // Validate delivery address if delivery mode
    if (fulfillmentType === 'delivery') {
      if (!deliveryAddress.line_1 || !deliveryAddress.post_town || !deliveryAddress.postcode.trim()) {
        newErrors.deliveryAddress = 'Address line one, post town and postcode are required'
        if (!firstErrorRef) firstErrorRef = deliveryAddressRef
      } else if (deliveryPostcodeStatus === 'not_found') {
        newErrors.deliveryAddress = POSTCODE_NOT_FOUND_MESSAGE
        if (!firstErrorRef) firstErrorRef = deliveryAddressRef
      }
    }

    // If there are errors, update state and scroll to first error
    if (
      newErrors.jobNumber ||
      newErrors.reasonForOrder ||
      newErrors.secondaryReason ||
      newErrors.deliveryAddress
    ) {
      setErrors(newErrors)

      // Scroll to first error field with smooth animation
      if (firstErrorRef?.current) {
        firstErrorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })

        // Focus the input field after scrolling
        setTimeout(() => {
          const input =
            firstErrorRef?.current?.querySelector('input') ||
            firstErrorRef?.current?.querySelector('select')
          if (input) {
            ;(input as HTMLElement).focus()
          }
        }, 500)
      }

      return
    }

    if (!confirmedSupplierOrder) {
      if (isCheckingSupplierWarningRef.current) return
      isCheckingSupplierWarningRef.current = true
      let shouldWarn: boolean
      try {
        shouldWarn = await shouldWarnBeforePlacing()
      } finally {
        isCheckingSupplierWarningRef.current = false
      }
      if (shouldWarn) {
        setShowSupplierOrderWarning(true)
        return
      }
    }

    setShowSupplierOrderWarning(false)
    setIsPlacingOrder(true)

    // DEV-52059: compute the delivery-postcode branch overrides from a FRESH
    // read of the side query. The reactive memo above may still be loading
    // when the engineer taps Place order (e.g. they changed the postcode a
    // moment before clicking), so we can't rely on its closure value here.
    // fetchQuery dedupes with the useQuery that's already running under the
    // same key and resolves with the real response. A failure falls back to
    // no override — same as pre-fix behaviour and matches the "warning never
    // blocks the order" rule the postcode check follows.
    let effectiveBranchOverrides: Map<string, DeliveryBranchOverride> = new Map()
    // Keep the raw delivery-postcode stock around (not just the overrides)
    // so the submission loop can also use its per-item date/stock/backorder
    // values for overridden groups (DEV-52059 option B).
    let effectiveDeliveryStock: DeliveryBranchStockEntry[] | null = null
    const canonicalPostcodeForFetch = settledDeliveryPostcode
    if (
      fulfillmentType === 'delivery' &&
      canonicalPostcodeForFetch &&
      postcodeRoutedStockItems.length > 0 &&
      normalizePostcode(canonicalPostcodeForFetch) !==
        normalizePostcode(session?.postcode ?? '')
    ) {
      try {
        const stockResp = await queryClient.fetchQuery({
          queryKey: [
            'checkout-stock-delivery-postcode',
            JSON.stringify(postcodeRoutedStockItems),
            canonicalPostcodeForFetch,
          ],
          queryFn: () =>
            api.getCartStock(postcodeRoutedStockItems, canonicalPostcodeForFetch),
          staleTime: 2 * 60 * 1000,
        })
        effectiveDeliveryStock =
          (stockResp?.stock as DeliveryBranchStockEntry[] | undefined) ?? null
        const { overrides } = computeDeliveryBranchResult(
          effectiveDeliveryStock ?? undefined,
          orders,
        )
        effectiveBranchOverrides = overrides
      } catch {
        // Lookup failed — submit with the job-postcode branch (same as before
        // this fix). The engineer has already seen the fallback warning in
        // the UI when this happens.
      }
    }

    try {
      // Place orders for each branch group
      const placedOrders: Array<{
        id: string
        orderNumber: string
        supplierName: string
        branchName: string
        total: number
        fulfillmentType: string
        itemCount: number
        submissionWarning?: string
        canRetry?: boolean
      }> = []

      for (const order of orders) {
        try {
          // Collect van stock item IDs for this branch
          const branchVanStockItems = order.items
            .filter(item => {
              const itemId = item.id || String(item.productId)
              return isItemVanStock(itemId)
            })
            .map(item => item.id || String(item.productId))

          // Whether THIS branch order is entirely van stock. Decided per order,
          // not from the whole checkout, so a pure van stock group in a mixed
          // multi-branch checkout does not inherit another group's job number or
          // reasons (mirrors the backend's per-order van stock check).
          const orderAllVanStock =
            order.items.length > 0 &&
            branchVanStockItems.length === order.items.length

          // DEV-52059: for CPS/Wolseley delivery groups, if the delivery
          // postcode routes to a different fulfilment branch than the job
          // postcode did, record the order on that delivery-postcode branch
          // (same supplier guaranteed). Cart-item lookup and role checks on
          // the backend still use the original branch_id — only Order.branch
          // changes, so the engineer and admins see the branch the supplier
          // actually fulfils from. Read from effectiveBranchOverrides (fresh
          // handler-side fetch) rather than the reactive memo so a lookup
          // that was still loading at click time has settled by now.
          const branchOverride = effectiveBranchOverrides.get(
            `${order.supplierId}-${order.branchId}`,
          )
          // Option B: when a group's branch is overridden, the dates / stock /
          // backorder values recorded against the order must come from the
          // delivery-postcode response for the SAME line (keyed by the item's
          // original branch_id — that's what we requested). A missing or
          // stock_unavailable row keeps the original value, matching the
          // "warning never blocks the order" rule from part 1.
          const applyDeliveryOverride =
            fulfillmentType === 'delivery' &&
            branchOverride != null &&
            effectiveDeliveryStock != null
          const deliveryOverrideByItemId = new Map<
            string,
            EffectiveDeliveryFromEntry
          >()
          if (applyDeliveryOverride) {
            for (const item of order.items) {
              if (!item.id) continue
              const entry = effectiveDeliveryStock!.find(
                (s) =>
                  String(s.raw_product_id) === String(item.productId) &&
                  String(s.branch_id) === String(item.branchId),
              )
              if (!entry || entry.stock_unavailable === true) continue
              deliveryOverrideByItemId.set(
                item.id,
                computeEffectiveDeliveryFromEntry(item, entry),
              )
            }
          }

          // DEV-51011: the same rollup the date row shows. expected_fulfillment_date
          // stays the supplier's latest line date as given (null when no line
          // has one); requested_fulfillment_date is set only when the engineer
          // picked a different day in the sheet. For an overridden group, each
          // line's deliveryOrderDate is swapped for the delivery-postcode
          // branch's value so expected_fulfillment_date reflects the branch the
          // supplier will actually fulfil from.
          const itemsForOrderDates = applyDeliveryOverride
            ? order.items.map((item) => {
                const override = item.id
                  ? deliveryOverrideByItemId.get(item.id)
                  : undefined
                return override
                  ? { ...item, deliveryOrderDate: override.deliveryOrderDate }
                  : item
              })
            : order.items
          const { latestItemDate, requestedDate } = checkoutOrderDates(
            itemsForOrderDates,
            fulfillmentType,
            order.preferredDate,
            chosenDates[orderKeyFor(order)],
          )

          // Stock available at checkout, keyed by cart-line id — stored on each
          // OrderItem as branch_stock_at_order so approvers can compare
          // was-vs-now (APR-003a AC2). Keyed per line (not raw_product_id): a
          // delivery order can hold two lines of the same product from different
          // original branches, which must not overwrite each other. Save the
          // SAME channel this order uses — delivery stock for a delivery order,
          // collection stock for a collection order — so the approval re-check
          // compares like-for-like. (branch_stock alone is branch-specific and
          // diverges for borrowed deliveries; the per-channel value is stable.)
          const stockAtOrder: Record<string, number> = {}
          order.items.forEach(item => {
            if (!item.id) return
            const override = applyDeliveryOverride
              ? deliveryOverrideByItemId.get(item.id)
              : undefined
            if (override) {
              stockAtOrder[item.id] = override.deliveryStock
              return
            }
            const stockEntry = liveStockData?.stock?.find(
              (s: {
                raw_product_id: number | string
                branch_id: string | number
                branch_stock?: number | null
                collection_stock?: number | null
                delivery_stock?: number | null
              }) =>
                String(s.raw_product_id) === String(item.productId) &&
                String(s.branch_id) === String(item.branchId),
            )
            const channelStock =
              fulfillmentType === 'delivery'
                ? stockEntry?.delivery_stock
                : stockEntry?.collection_stock
            const stockValue = channelStock ?? stockEntry?.branch_stock
            if (stockValue != null) {
              stockAtOrder[item.id] = stockValue
            }
          })

          // Per-item expected date the engineer saw at checkout, keyed by
          // cart-line id — stored on each OrderItem as expected_date_at_order so
          // approval can show a per-item date change (APR-011 AC1). Keyed per
          // line (not raw_product_id) for the same duplicate-line reason as the
          // stock snapshot above. Uses the same channel-aware date as the rollup.
          const datesAtOrder: Record<string, string> = {}
          order.items.forEach(item => {
            if (!item.id) return
            const override = applyDeliveryOverride
              ? deliveryOverrideByItemId.get(item.id)
              : undefined
            const itemDate = override
              ? override.deliveryOrderDate
              : fulfillmentType === 'pickup'
                ? item.pickupOrderDate
                : item.deliveryOrderDate
            if (
              typeof itemDate === 'string' &&
              itemDate.length > 0 &&
              !Number.isNaN(new Date(itemDate).getTime())
            ) {
              datesAtOrder[item.id] = itemDate
            }
          })

          // Backorder lines, keyed by cart-line id: units above the channel's
          // stock (branch + depot + borrowed) and the backorder date shown.
          // The backend validates each against the supplier/product policy and
          // records them on the order line. Absent for normal lines. For an
          // overridden group, the backorder test is re-run against the
          // delivery-postcode branch's stock and backorder date.
          const backordersAtOrder: Record<string, { quantity: number; date: string | null }> = {}
          order.items.forEach(item => {
            if (!item.id) return
            const override = applyDeliveryOverride
              ? deliveryOverrideByItemId.get(item.id)
              : undefined
            if (override) {
              if (!override.isDeliveryBackorder) return
              const backorderUnits = Math.max(
                item.quantity - override.deliveryStock,
                0,
              )
              if (backorderUnits <= 0) return
              backordersAtOrder[item.id] = {
                quantity: backorderUnits,
                date: override.backorderDeliveryDate,
              }
              return
            }
            if (!canBackorderLine(item, fulfillmentType)) return
            const channelOption = item.fulfillmentOptions?.find(
              opt => opt.type === fulfillmentType,
            )
            const channelStock = channelOption?.stockQuantity ?? 0
            const backorderUnits = Math.max(item.quantity - channelStock, 0)
            if (backorderUnits <= 0) return
            backordersAtOrder[item.id] = {
              quantity: backorderUnits,
              date:
                fulfillmentType === 'pickup'
                  ? (item.backorderCollectionDate ?? null)
                  : (item.backorderDeliveryDate ?? null),
            }
          })

          // Create one order for this group from its exact items, on its
          // (delivering, on the delivery tab) branch. The backend validates
          // each item's own branch for access.
          const response = await api.createOrder({
            branch_id: String(order.branchId),
            order_branch_id: branchOverride?.newBranchId,
            cart_item_ids: order.items
              .map(i => i.id)
              .filter((id): id is string => !!id),
            fulfillment_type: fulfillmentType === 'pickup' ? 'collection' : 'delivery',
            // DEV-51047: only delivery orders use the editable postcode. A
            // collection order still sends the job postcode, exactly as before
            // the field became editable (some suppliers put it in billing
            // fields even on collections).
            delivery_address:
              fulfillmentType === 'pickup'
                ? { ...deliveryAddress, postcode: session?.postcode ?? deliveryAddress.postcode }
                : deliveryAddress,
            // The Job Details fields are omitted for a pure van stock order, per
            // branch group, so a van stock group in a mixed checkout never carries
            // another group's job details (the backend defaults its reason to
            // "Van Stock Replenishment"). Values are kept in state, not cleared,
            // so they survive a toggle-off or a rolled-back van stock toggle.
            job_number: orderAllVanStock ? undefined : jobNumber.trim() || undefined,
            reason: orderAllVanStock ? undefined : reasonForOrder || undefined,
            secondary_reason: orderAllVanStock ? undefined : secondaryReason || undefined,
            van_stock_items: branchVanStockItems,
            expected_fulfillment_date: latestItemDate,
            requested_fulfillment_date: requestedDate,
            stock_at_order: stockAtOrder,
            dates_at_order: datesAtOrder,
            backorders_at_order:
              Object.keys(backordersAtOrder).length > 0 ? backordersAtOrder : undefined,
            // Ties this order back to the checkout_started event. Sent on
            // every createOrder call so the join still holds if a basket ever
            // splits into more than one order again.
            checkout_id: checkoutIdRef.current ?? undefined,
          })

          placedOrders.push({
            id: response.id,
            orderNumber: response.order_number,
            supplierName: order.supplierName,
            branchName: order.branchName,
            total: order.total,
            fulfillmentType,
            itemCount: order.items.length,
            submissionWarning: response.submission_warning,
            canRetry: response.can_retry,
          })

          // Show warning toast if submission had issues but order was created
          if (response.submission_warning) {
            addToast(
              `Order# ${response.order_number} created but submission pending`,
              'warning',
              5000
            )
          }
        } catch (orderError: unknown) {
          const apiError = orderError as { status?: number; data?: Record<string, unknown> }
          const errorMessage =
            (typeof apiError?.data?.error === 'string' && apiError.data.error) ||
            (typeof apiError?.data?.detail === 'string' && apiError.data.detail) ||
            (orderError instanceof Error ? orderError.message : 'Unknown error')
          console.error('Failed to create order for branch:', order.branchName, orderError)
          addToast(
            `Failed to place order for ${order.branchName}: ${errorMessage}`,
            'error',
            5000
          )
        }
      }

      // If at least one order was placed successfully
      if (placedOrders.length > 0) {
        // Set flag to prevent redirect to cart when cart becomes empty
        setHasPlacedOrder(true)

        // The basket was submitted, so this checkout's id is done: clear it so
        // the next checkout of this branch starts a fresh funnel (and an
        // unmatched checkout_started stays the derived abandonment signal).
        if (checkoutScopeRef.current) {
          clearCheckoutId(checkoutScopeRef.current)
        }

        // Invalidate cart cache so it refetches (backend clears cart items after order)
        await queryClient.invalidateQueries({ queryKey: ['cart'] })
        // Refresh the weekly-spend tracker — the just-placed order now counts
        // toward this week's spend (APR-012).
        queryClient.invalidateQueries({ queryKey: ['weekly-spend'] })

        // Navigate to confirmation page with order IDs in URL
        const orderIds = placedOrders.map((order) => order.id).join(',')
        navigate(`/${tenantSlug}/engineer/order-confirmation?orders=${orderIds}`)
      } else if (orders.length > 1) {
        // Only show generic message for multi-order checkout (per-order errors already shown)
        addToast('All orders failed. Please try again.', 'error', 5000)
      }
    } catch (error) {
      console.error('Order placement error:', error)
      addToast('An error occurred while placing your order', 'error', 5000)
    } finally {
      setIsPlacingOrder(false)
    }
  }

  // Show loading while cart is being fetched
  if (isLoadingCart) {
    return (
      <EngineerLayout hideHeader>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
        </div>
      </EngineerLayout>
    )
  }

  if (!session) {
    return null
  }

  // Only show "No items available" after stock data has loaded
  // This prevents flashing empty state while checking stock
  if (orders.length === 0 && !isLoadingStock && !isLoadingPrice && cart.length > 0) {
    return (
      <EngineerLayout hideHeader>
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-gray-900 text-xl font-bold mb-2">
            No items available for {fulfillmentType === 'pickup' ? 'collection' : 'delivery'}
          </h2>
          <p className="text-gray-500 text-sm mb-6 text-center">
            The items in your basket are not available for {fulfillmentType === 'pickup' ? 'collection' : 'delivery'}. Please select a
            different method.
          </p>
          <Button onClick={() => navigate(`/${tenantSlug}/engineer/cart`)}>
            Back to basket
          </Button>
        </div>
      </EngineerLayout>
    )
  }

  return (
    <EngineerLayout hideHeader>
      {/* Header — themed via `.bg-nav` / `.text-nav`. Shared EngineerHeader
       * suppressed: only the back+title bar (with weekly ring) renders. */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/${tenantSlug}/engineer/cart`)}
              className="p-2 -ml-2 text-nav hover:bg-nav-hover rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-nav font-bold flex-1">
              Checkout {orders.length > 1 && `(${orders.length} Orders)`}
            </h1>
            {/* Weekly spend ring (APR-012 AC2) — spend excludes this order.
                Hidden entirely when the role has no weekly cap (AC3). */}
            {weeklySpend?.has_cap && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="text-right">
                  <span className="text-[11px] text-nav-inactive block leading-none">Weekly</span>
                  {/* Whole-pound display so the Checkout ring matches the
                   * Home tab, the mockup, and the shared WeeklySpendGauge. */}
                  <span className="text-xs tabular-nums font-bold text-nav">
                    £{Math.round(weeklySpent)}
                    <span className="text-nav-inactive font-normal"> / £{Math.round(weeklyCap)}</span>
                  </span>
                </div>
                <div className="w-12 h-12 relative">
                  <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                    <circle
                      cx="18"
                      cy="18"
                      r="15"
                      fill="none"
                      stroke="currentColor"
                      strokeOpacity={0.2}
                      strokeWidth="3"
                    />
                    <circle
                      cx="18"
                      cy="18"
                      r="15"
                      fill="none"
                      stroke="#fbbf24"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray={`${weeklyRingPct * 0.94} ${100 - weeklyRingPct * 0.94}`}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-nav">
                    {weeklyRingPct}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Fulfillment Badge. Icon rendered neutral (`text-gray-700`) to
         * match the mockup — the badge is metadata, not a CTA, so it
         * shouldn't compete with the "Place Order" button for pink. */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-center gap-2">
            {fulfillmentType === 'pickup' ? (
              <>
                <Package className="w-5 h-5 text-gray-700" />
                <span className="text-gray-900 font-bold text-lg uppercase">
                  Collection
                </span>
              </>
            ) : (
              <>
                <Truck className="w-5 h-5 text-gray-700" />
                <span className="text-gray-900 font-bold text-lg uppercase">
                  Delivery
                </span>
              </>
            )}
          </div>
        </div>

        {/* Price Change Warning */}
        {!isLoadingPrice && cartWithLiveData.some(item => item.priceChanged) && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-900 font-semibold text-sm mb-1">
                  Price update detected
                </p>
                <p className="text-amber-700 text-xs mb-2">
                  Some item prices have changed since you added them to your basket. The updated prices are shown below.
                </p>
                <div className="space-y-1">
                  {cartWithLiveData
                    .filter(item => item.priceChanged)
                    .map(item => (
                      <div key={item.id} className="text-xs text-amber-800">
                        <span className="font-medium">{item.productName}</span>
                        {' - '}
                        <span className="line-through">£{item.priceAtAdded?.toFixed(2) || '0.00'}</span>
                        {' → '}
                        <span className="font-semibold">£{item.price.toFixed(2)}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Out of Stock Warning */}
        {!isLoadingStock && cartWithLiveData.some(item =>
          !canBackorderLine(item, fulfillmentType) &&
          item.fulfillmentOptions?.every(opt => opt.stockStatus === 'out_of_stock')
        ) && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-900 font-semibold text-sm mb-1">
                  Stock unavailable
                </p>
                <p className="text-red-700 text-xs mb-2">
                  Some items are now out of stock and cannot be ordered:
                </p>
                <div className="space-y-1">
                  {cartWithLiveData
                    .filter(item =>
                      !canBackorderLine(item, fulfillmentType) &&
                      item.fulfillmentOptions?.every(opt => opt.stockStatus === 'out_of_stock'))
                    .map(item => (
                      <div key={item.id} className="text-xs text-red-800 font-medium">
                        • {item.productName}
                      </div>
                    ))}
                </div>
                <p className="text-red-700 text-xs mt-2">
                  These items have been excluded from your order.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Expand All / Collapse All Buttons (only if van stock products exist) */}
        {hasAnyVanStockProducts && orders.length > 1 && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={expandAllOrders}
              className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Expand all
            </button>
            <button
              onClick={collapseAllOrders}
              className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Collapse all
            </button>
          </div>
        )}

        {/* Order Summaries */}
        <div className="space-y-4 mb-6">
          {orders.map((order, idx) => {
            const orderKey = orderKeyFor(order)
            const isExpanded = expandedOrders.has(orderKey)
            // The group already IS the delivering branch (see groupOrdersByBranch).
            const headerBranchName = order.branchName
            const headerDistanceMiles = order.distanceMiles

            // Calculate van stock composition for this order (only if van stock products exist)
            const orderVanStockCount = hasAnyVanStockProducts
              ? order.items.filter(item => {
                  const itemId = item.id || String(item.productId)
                  return isItemVanStock(itemId)
                }).length
              : 0
            const orderJobItemCount = order.items.length - orderVanStockCount

            // DEV-52059: when this CPS/Wolseley group's branch is overridden
            // under the delivery postcode, use that branch's per-line dates
            // for the date row so the engineer sees the day the supplier
            // actually fulfils against — matching what we send on submit.
            // Nothing else on the card changes (price, line details, backorder
            // pill), so the engineer isn't surprised mid-review. A line with
            // no / unavailable delivery-postcode entry keeps its original date.
            const dateRowOverride = deliveryBranchOverrides.get(
              `${order.supplierId}-${order.branchId}`,
            )
            const applyDateRowOverride =
              fulfillmentType === 'delivery' &&
              dateRowOverride != null &&
              deliveryBranchStockData?.stock != null
            const itemsForDateRow = applyDateRowOverride
              ? order.items.map((item) => {
                  const entry = (
                    deliveryBranchStockData.stock as DeliveryBranchStockEntry[]
                  ).find(
                    (s) =>
                      String(s.raw_product_id) === String(item.productId) &&
                      String(s.branch_id) === String(item.branchId),
                  )
                  if (!entry || entry.stock_unavailable === true) return item
                  const { deliveryOrderDate } = computeEffectiveDeliveryFromEntry(
                    item,
                    entry,
                  )
                  return { ...item, deliveryOrderDate }
                })
              : order.items

            // DEV-51011: one date per order card, from the lines that will be
            // placed. pickupOrderDate / deliveryOrderDate already carry the
            // backorder date on backorder lines (DEV-51018), so a backorder
            // card gets its SLA date and an in-stock card never does.
            const orderDates = checkoutOrderDates(
              itemsForDateRow,
              fulfillmentType,
              order.preferredDate,
              chosenDates[orderKey],
            )
            const dateRow = (
              <FulfillmentDateRow
                fulfillmentType={fulfillmentType}
                bookedDate={orderDates.bookedDate}
                earliestDate={orderDates.earliest}
                lastChoosableDate={orderDates.lastChoosable}
                isConfirmed={orderDates.isConfirmed}
                canChange={orderDates.canChange}
                isLoading={isLoadingStock}
                onChange={(date) =>
                  setChosenDates((previous) => ({ ...previous, [orderKey]: date }))
                }
              />
            )

            return (
              <div key={orderKey}>
                <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-hidden">
                  {/* Collapsible Header */}
                  <button
                    onClick={() => toggleOrder(orderKey)}
                    className="w-full text-left bg-gray-50 px-4 sm:px-5 py-4 border-b border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {orders.length > 1 && (
                            <span className="text-brand-primary font-bold text-sm">#{idx + 1}</span>
                          )}
                          <h3 className="text-gray-900 font-bold text-base">
                            {headerBranchName}
                          </h3>
                          {/* DEV-51018 AC8: subtle pill marks the
                              backorder order so its # card can't be
                              mistaken for a duplicate of the in-stock
                              sibling. Card styling stays neutral to
                              match the cart's approach. */}
                          {order.isBackorder && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800 uppercase tracking-wide">
                              Backorder
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="truncate text-gray-600">{order.supplierName}</span>
                            {headerDistanceMiles != null && (
                              <>
                                <span className="text-gray-400">•</span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <MapPin className="w-3.5 h-3.5 text-blue-600" />
                                  <span className="font-medium text-blue-600">{headerDistanceMiles.toFixed(1)} mi</span>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm flex-wrap">
                            <span className="text-gray-400 hidden sm:inline">•</span>
                            <span className="font-medium text-gray-700">{order.items.length} {order.items.length === 1 ? 'item' : 'items'}</span>
                            {orderVanStockCount > 0 && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="inline-flex items-center gap-1 text-xs text-violet-700 font-medium">
                                  <Package className="w-3 h-3" />
                                  {orderVanStockCount} van stock
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="flex items-center gap-2 justify-end">
                            <p className="text-gray-900 font-bold text-lg">
                              {order.hasUnavailablePrice ? <span className="text-gray-400">£-</span> : `£${order.total.toFixed(2)}`}
                            </p>
                            {order.hasUnavailablePrice && (
                              <span className="text-amber-600 text-[10px]">(Price unavailable)</span>
                            )}
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expandable Content */}
                  {isExpanded && (
                    <div>

                {/* Order Items */}
                <div className="divide-y divide-gray-100">
                  {order.items.map((item) => (
                    <div
                      key={`${item.productId}-${item.branchId}`}
                      className="p-4 sm:p-5 flex gap-4"
                    >
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.productName}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }}
                          />
                        ) : null}
                        <div className={item.imageUrl ? 'hidden flex flex-col items-center justify-center' : 'flex flex-col items-center justify-center'}>
                          <Package className="w-6 h-6 text-gray-300" />
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-1">
                          <h4 className="text-gray-900 font-semibold text-sm flex-1">
                            {item.productName}
                          </h4>
                          {item.priceChanged && (
                            <span className="inline-flex items-center gap-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold flex-shrink-0">
                              PRICE UPDATED
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs mb-2">
                          {item.supplierProductCode}
                        </p>
                        <div className="flex items-center justify-between mb-2">
                          <span className={clsx(
                            "text-sm",
                            item.priceChanged ? "text-amber-700 font-semibold" : "text-gray-600"
                          )}>
                            {item.priceUnavailable ? <span className="text-gray-400">£- × {item.quantity}</span> : `£${perUnitPrice(item).toFixed(2)} × ${item.quantity}`}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={clsx(
                              "font-bold text-base",
                              item.priceChanged ? "text-amber-700" : item.priceUnavailable ? "text-gray-400" : "text-gray-900"
                            )}>
                              {item.priceUnavailable ? '£-' : `£${roundToPenny(perUnitPrice(item) * item.quantity).toFixed(2)}`}
                            </span>
                            {item.priceUnavailable && (
                              <span className="text-amber-600 text-xs">(Price unavailable)</span>
                            )}
                          </div>
                        </div>

                        {/* Van Stock Toggle — only on admin-designated van stock items */}
                        {item.isVanStock && (() => {
                          const itemId = item.id || String(item.productId)
                          const isVanStock = isItemVanStock(itemId)
                          return (
                            <VanStockToggle
                              enabled={isVanStock}
                              onChange={() => handleVanStockToggle(itemId)}
                            />
                          )
                        })()}
                      </div>
                    </div>
                  ))}
                </div>

                      {/* Order Total */}
                      <div className="bg-gray-50 px-4 sm:px-5 py-3 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm">Subtotal</span>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900 font-bold text-lg">
                              {order.hasUnavailablePrice ? <span className="text-gray-400">£-</span> : `£${order.total.toFixed(2)}`}
                            </span>
                            {order.hasUnavailablePrice && (
                              <span className="text-amber-600 text-xs">(Price unavailable)</span>
                            )}
                          </div>
                        </div>
                      </div>

                    </div>
                  )}

                  {/* Pickup Location (for pickup mode only) — always visible, outside collapsible.
                   * Header icon + phone link render in neutral gray to match the mockup: these are
                   * informational labels, not interactive brand CTAs, so applying brand-primary was
                   * over-tinting the checkout flow. */}
                  {fulfillmentType === 'pickup' && (
                    <div className="px-4 sm:px-5 py-4 bg-blue-50 border-t border-blue-100">
                      <h3 className="text-gray-900 font-semibold text-sm mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        Collection location
                      </h3>
                      <div className="bg-white rounded-lg border border-gray-200 p-3">
                        <p className="text-gray-900 font-semibold mb-1">{order.branchName}</p>
                        {order.branchAddressLine1 && (
                          <p className="text-gray-600 text-sm mb-0.5">{order.branchAddressLine1}</p>
                        )}
                        {order.branchAddressLine2 && (
                          <p className="text-gray-600 text-sm mb-0.5">{order.branchAddressLine2}</p>
                        )}
                        {(order.branchCity || order.branchPostcode) && (
                          <p className="text-gray-600 text-sm mb-2">
                            {[order.branchCity, order.branchPostcode].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {order.branchPhone && (
                          <a
                            href={`tel:${order.branchPhone}`}
                            className="flex items-center gap-2 text-gray-600 text-sm font-medium hover:text-gray-900"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            {order.branchPhone}
                          </a>
                        )}
                      </div>
                      {/* DEV-51011: below the branch card, not inside it — the
                       * card's own padding would leave the row 28px narrower
                       * than the mock's and wrap the "Earliest" tag. */}
                      <div className="mt-3">{dateRow}</div>
                    </div>
                  )}

                  {/* DEV-51011: delivery orders get the same date row. The
                   * delivery address is one form for the whole checkout, but
                   * each order card books its own day, so the row sits here. */}
                  {fulfillmentType === 'delivery' && (
                    <div className="px-4 sm:px-5 py-4 border-t border-gray-100">{dateRow}</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>


        {/* Job Details — hidden when all items are van stock */}
        {!allItemsVanStock && (
          <div className="mb-6">
            <h2 className="text-gray-900 font-bold text-lg mb-3">Job details</h2>
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
              <div ref={jobNumberRef} className={errors.jobNumber ? 'animate-shake' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job number
                  {orderConfig.job_number_requirement === 'required' && (
                    <span className="text-red-500 ml-0.5">*</span>
                  )}
                </label>
                <input
                  value={jobNumber}
                  onChange={(e) => {
                    setJobNumber(e.target.value)
                    if (errors.jobNumber) setErrors((prev) => ({ ...prev, jobNumber: '' }))
                  }}
                  placeholder={orderConfig.job_number_placeholder || 'e.g. J-A1B2C3D4'}
                  className={clsx(
                    'w-full h-14 px-4 text-base text-gray-900 bg-white border rounded-xl',
                    'placeholder:text-gray-400 focus:outline-none focus:ring-2',
                    errors.jobNumber
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-brand-primary focus:border-brand-primary'
                  )}
                />
                {errors.jobNumber && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.jobNumber}</p>
                )}
              </div>

              {/* Reason for Order - Dynamic based on tenant config */}
              {orderConfig.reason_field_type !== 'hidden' && (
                <div ref={reasonRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Order
                    {orderConfig.reason_field_requirement === 'required' && (
                      <span className="text-red-500 ml-0.5">*</span>
                    )}
                  </label>
                  <div className={errors.reasonForOrder ? 'animate-shake' : ''}>
                    {orderConfig.reason_field_type === 'dropdown' &&
                    orderConfig.reason_dropdown_options?.length ? (
                      <select
                        value={reasonForOrder}
                        onChange={(e) => {
                          setReasonForOrder(e.target.value)
                          // Reset dependent secondary reason whenever the
                          // primary reason changes — options may differ per
                          // primary and we don't want a stale selection.
                          setSecondaryReason('')
                          if (errors.reasonForOrder)
                            setErrors((prev) => ({ ...prev, reasonForOrder: '' }))
                          if (errors.secondaryReason)
                            setErrors((prev) => ({ ...prev, secondaryReason: '' }))
                        }}
                        className={clsx(
                          'w-full h-14 px-4 text-base text-gray-900 bg-white border rounded-xl',
                          'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
                          'appearance-none cursor-pointer',
                          errors.reasonForOrder
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300'
                        )}
                      >
                        <option value="">Select a reason...</option>
                        {orderConfig.reason_dropdown_options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <textarea
                        value={reasonForOrder}
                        onChange={(e) => {
                          setReasonForOrder(e.target.value)
                          if (errors.reasonForOrder)
                            setErrors((prev) => ({ ...prev, reasonForOrder: '' }))
                        }}
                        placeholder="e.g. Emergency repair, Routine maintenance, New installation..."
                        rows={3}
                        className={clsx(
                          'w-full px-4 py-3 text-base text-gray-900 bg-white border rounded-xl placeholder:text-gray-400',
                          'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
                          'resize-none',
                          errors.reasonForOrder
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300'
                        )}
                      />
                    )}
                  </div>
                  {errors.reasonForOrder && (
                    <p className="text-red-600 text-xs mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.reasonForOrder}
                    </p>
                  )}

                  {/* Secondary Reason — cascading category dropdown grouped
                      under the same "Reason for Order" label. Shown only when
                      the primary reason is a dropdown, a value is selected,
                      and the tenant configured secondary options under THAT
                      specific primary in the Django admin
                      (TenantOrderConfig.secondary_reason_dropdown_options is a
                      dict keyed by primary reason value). */}
                  {(() => {
                    const secondaryOptions =
                      orderConfig.secondary_reason_dropdown_options?.[reasonForOrder] ?? []
                    if (
                      orderConfig.reason_field_type !== 'dropdown' ||
                      !reasonForOrder ||
                      secondaryOptions.length === 0
                    ) {
                      return null
                    }
                    return (
                      <div className="mt-2">
                        <div className={errors.secondaryReason ? 'animate-shake' : ''}>
                          <select
                            value={secondaryReason}
                            onChange={(e) => {
                              setSecondaryReason(e.target.value)
                              if (errors.secondaryReason)
                                setErrors((prev) => ({ ...prev, secondaryReason: '' }))
                            }}
                            className={clsx(
                              'w-full h-14 px-4 text-base text-gray-900 bg-white border rounded-xl',
                              'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
                              'appearance-none cursor-pointer',
                              errors.secondaryReason
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300'
                            )}
                          >
                            <option value="">Select secondary reason...</option>
                            {secondaryOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {errors.secondaryReason && (
                          <p className="text-red-600 text-xs mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.secondaryReason}
                          </p>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delivery Address Form */}
        {fulfillmentType === 'delivery' && (
          <div className="mb-6" ref={deliveryAddressRef}>
            <h2 className="text-gray-900 font-bold text-lg mb-3">Delivery address</h2>
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
              {errors.deliveryAddress && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2 animate-shake">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{errors.deliveryAddress}</p>
                </div>
              )}

              {/* Address Lookup and Fields */}
              <UKAddressLookup
                postcode={addressListPostcode}
                value={deliveryAddress}
                onChange={(address) => {
                  setDeliveryAddress(address)
                  if (errors.deliveryAddress) {
                    setErrors((prev) => ({ ...prev, deliveryAddress: '' }))
                  }
                }}
                // Locked while Place order waits on the postcode check: the
                // running handler submits the address as it was when tapped,
                // so edits made during the wait would be silently dropped.
                disabled={isSettlingPostcode}
                errors={{
                  line_1: errors.deliveryAddress && !deliveryAddress.line_1 ? 'Required' : undefined,
                  post_town: errors.deliveryAddress && !deliveryAddress.post_town ? 'Required' : undefined,
                  postcode:
                    errors.deliveryAddress && !deliveryAddress.postcode.trim()
                      ? 'Required'
                      : deliveryPostcodeCheck.status === 'not_found'
                        ? POSTCODE_NOT_FOUND_MESSAGE
                        : undefined,
                }}
                isPostcodeChecking={deliveryPostcodeCheck.status === 'checking'}
                postcodeHelp={
                  <DeliveryPostcodeNotices
                    workingPostcode={session.postcode}
                    deliveryPostcode={deliveryAddress.postcode}
                    status={deliveryPostcodeCheck.status}
                    canonicalPostcode={deliveryPostcodeCheck.postcode}
                    jobCoordinates={
                      Number.isFinite(session.latitude) && Number.isFinite(session.longitude)
                        ? { latitude: session.latitude, longitude: session.longitude }
                        : null
                    }
                    deliveryCoordinates={deliveryPostcodeCheck.coordinates}
                    branchChanges={Array.from(deliveryBranchOverrides.values()).map((o) => ({
                      supplierName: o.supplierName,
                      oldBranchName: o.oldBranchName,
                      newBranchName: o.newBranchName,
                    }))}
                    branchLookupFailed={deliveryBranchLookupFailed}
                  />
                }
              />
            </div>
          </div>
        )}

      </main>

      {/* Sticky Footer - Enhanced */}
      <div ref={toastClearanceRef} className="fixed bottom-16 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-lg z-50">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-gray-900 font-bold text-2xl">
                  {hasAnyUnavailablePrice ? '-' : `£${grandTotal.toFixed(2)}`}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-600">{orders.length}</span> {orders.length === 1 ? 'order' : 'orders'}
                <span className="mx-1.5 text-gray-300">·</span>
                <span className="font-medium text-gray-600">{totalItems}</span> {totalItems === 1 ? 'item' : 'items'}
                <span className="mx-1.5 text-gray-300">·</span>
                <span className="capitalize">{fulfillmentType === 'pickup' ? 'collection' : 'delivery'}</span>
              </p>
              {unmergedProductNames.length > 0 && (
                <p className="mt-1 text-xs font-medium text-red-600">
                  {`Couldn't combine the ${unmergedProductNames.join(', ')} lines. Go back to the basket and adjust them.`}
                </p>
              )}
            </div>
            <Button
              onClick={() => void handlePlaceOrder()}
              disabled={
                isPlacingOrder ||
                isSettlingPostcode ||
                isMergeSettling ||
                unmergedProductNames.length > 0
              }
              size="lg"
              className="flex-shrink-0 min-w-[140px]"
            >
              {isPlacingOrder
                ? 'Placing…'
                : isSettlingPostcode || isMergeSettling
                  ? 'Checking…'
                  : orders.length === 1
                    ? 'Place order'
                    : `Place all →`}
            </Button>
          </div>
        </div>
      </div>
      <Dialog.Root
        open={showSupplierOrderWarning}
        onOpenChange={(open) => {
          if (!open && !isPlacingOrder) setShowSupplierOrderWarning(false)
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <ShieldAlert className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <Dialog.Title className="text-gray-900 font-bold text-lg mb-1">
                    Confirm supplier order
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-600 text-sm leading-relaxed">
                    This environment is connected to live supplier ordering. Continuing will submit this order to the supplier and may create a real production order.
                  </Dialog.Description>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-amber-900 text-sm font-medium">
                  Only continue if you intend to place a real supplier order.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSupplierOrderWarning(false)}
                  disabled={isPlacingOrder}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-50 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => void handlePlaceOrder(true)}
                  disabled={isPlacingOrder}
                  className="flex-1 px-4 py-3 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 disabled:opacity-50 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isPlacingOrder ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Placing…
                    </>
                  ) : (
                    'Place order'
                  )}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root
        open={showPhoneDialog}
        onOpenChange={(open) => { if (!open && !isSavingPhone) setShowPhoneDialog(false) }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <Dialog.Title className="text-gray-900 font-bold text-lg mb-1">
                    Add your phone number
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-600 text-sm">
                    Suppliers need a contact number in case there's an issue with your order.
                  </Dialog.Description>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="e.g. 07700 900000"
                  value={phoneValue}
                  autoFocus
                  onChange={(e) => {
                    setPhoneValue(e.target.value)
                    if (phoneError) setPhoneError('')
                  }}
                  onBlur={() => setPhoneError(validatePhone(phoneValue))}
                  className={clsx(
                    'w-full px-4 py-3 rounded-xl border text-gray-900 text-base focus:outline-none focus:ring-2 transition-colors',
                    phoneError
                      ? 'border-red-400 focus:ring-red-400/30'
                      : 'border-gray-300 focus:ring-brand-primary/30 focus:border-brand-primary'
                  )}
                />
                {phoneError ? (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {phoneError}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">UK phone (mobile or landline). Accepts 07…, 020…, +44…, etc.</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPhoneDialog(false)}
                  disabled={isSavingPhone}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-50 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePhone}
                  disabled={isSavingPhone || !!phoneError}
                  className="flex-1 px-4 py-3 bg-brand-primary hover:bg-brand-primary-hover active:bg-brand-primary-dark disabled:opacity-50 text-[var(--brand-primary-text)] font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isSavingPhone ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Saving…
                    </>
                  ) : (
                    'Save & Place Order'
                  )}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </EngineerLayout>
  )
}
